import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { VitoguideBackendSettingsApiPicker } from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { UIService } from '@services';

import { UnitsActions } from './units.actions';
import { SettingsPreferences } from './units.state';
import { UnitsStore } from './units.store';

@Injectable()
export class UnitsEffects {
  getSettingPreferences$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UnitsActions.getSettingsPreferences),
      switchMap(() => {
        return this.settingsApi
          .instance()
          .getSettingsPreferences()
          .pipe(
            map((response) => {
              return UnitsActions.getSettingsPreferencesSuccess({
                preferences: response.preferences,
              });
            }),
            catchError(() => of(UnitsActions.getSettingPreferencesError()))
          );
      })
    );
  });

  setSettingPreferences$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UnitsActions.setSettingsPreferences),
      concatLatestFrom(() => this.unitsStore.settingsPreferences$),
      switchMap(([action, preferences]) => {
        this.uiService.blockUI();

        const newPreferences: SettingsPreferences = { ...preferences };
        newPreferences[action.preference as string] = action.value;

        return this.settingsApi
          .instance()
          .setSettingsPreferences(newPreferences)
          .pipe(
            map(() => {
              this.uiService.unblockUI();
              this.toast.info({
                message: this.translate.instant('+SETTINGS.UNITS.UNIT_CHANGE_SUCCESS'),
              });

              return UnitsActions.setSettingsPreferencesSuccess({ preferences: newPreferences });
            }),
            catchError(() => {
              this.uiService.unblockUI();
              this.toast.error({
                message: this.translate.instant('+SETTINGS.UNITS.UNIT_CHANGE_FAILURE'),
              });

              return of(UnitsActions.setSettingsPreferencesError());
            })
          );
      })
    );
  });

  constructor(
    private settingsApi: VitoguideBackendSettingsApiPicker,
    private actions$: Actions,
    private toast: ViToastService,
    private uiService: UIService,
    private translate: TranslateService,
    private unitsStore: UnitsStore
  ) {}
}
